import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { Skeleton } from '../ui/Skeleton';
import { ResponsiveSelect, type ResponsiveSelectOption } from '../ui/ResponsiveSelect';

type ServiceTarget = {
  serviceId: string;
  displayName?: string;
  environment: string;
  url: string;
  downtime_severity?: 'alert' | 'alarm';
  labels?: Record<string, string>;
  notifications?: {
    teams?: {
      alerts_channel?: string;
      alarms_channel?: string;
    };
  };
};

export function MonitoringServicesDialog(props: {
  open: boolean;
  tenantCode: string | null;
  onClose: () => void;
  onSaved: (tenantCode: string, newMonitoring: any) => void;
}) {
  const { open, tenantCode, onClose, onSaved } = props;
  const effectiveTenant = useMemo(() => String(tenantCode || '').toLowerCase(), [tenantCode]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [monitoring, setMonitoring] = useState<any>({});
  const [services, setServices] = useState<ServiceTarget[]>([]);
  const [teamsChannels, setTeamsChannels] = useState<ResponsiveSelectOption[]>([]);

  useEffect(() => {
    if (!open || !effectiveTenant) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await apiClient.getMonitoringTenantConfig(effectiveTenant);
        if (cancelled) return;
        const m = res?.monitoring || {};
        setMonitoring(m);
        setServices(Array.isArray(m.services) ? m.services : []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load services');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, effectiveTenant]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.getMonitoringMasterConfig();
        if (cancelled) return;
        const channels = res?.monitoring?.notifications?.teams?.channels || {};
        const keys = Object.keys(channels || {}).map((k) => String(k).trim()).filter(Boolean).sort();
        setTeamsChannels(keys.map((k) => ({ value: k, label: k })));
      } catch {
        // best-effort: channel picker will still allow manual YAML edits elsewhere
        if (!cancelled) setTeamsChannels([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const addRow = () => {
    setServices((prev) => [
      ...prev,
      { serviceId: '', displayName: '', environment: 'prod', url: '', downtime_severity: 'alert' },
    ]);
  };

  const updateRow = (idx: number, patch: Partial<ServiceTarget>) => {
    setServices((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const updateRowTeamsChannel = (idx: number, patch: { alerts_channel?: string; alarms_channel?: string }) => {
    setServices((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const prevTeams = r.notifications?.teams || {};
        const nextTeams = {
          ...prevTeams,
          ...(patch.alerts_channel !== undefined ? { alerts_channel: patch.alerts_channel || undefined } : {}),
          ...(patch.alarms_channel !== undefined ? { alarms_channel: patch.alarms_channel || undefined } : {}),
        };
        const hasAny = !!(nextTeams.alerts_channel || nextTeams.alarms_channel);
        return {
          ...r,
          notifications: hasAny ? { ...(r.notifications || {}), teams: nextTeams } : undefined,
        };
      }),
    );
  };

  const removeRow = (idx: number) => {
    setServices((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = (): string | null => {
    const seen = new Set<string>();
    for (const r of services) {
      const serviceId = String(r.serviceId || '').trim();
      const env = String(r.environment || '').trim();
      const url = String(r.url || '').trim();
      if (!serviceId) return 'Every service must have a serviceId.';
      if (!env) return 'Every service must have an environment.';
      if (!url) return 'Every service must have a url.';
      const key = `${serviceId}::${env}`;
      if (seen.has(key)) return `Duplicate service: ${serviceId} (${env}).`;
      seen.add(key);
    }
    return null;
  };

  const save = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = { ...(monitoring || {}) };
      next.services = services.map((s) => ({
        serviceId: String(s.serviceId || '').trim(),
        environment: String(s.environment || '').trim(),
        url: String(s.url || '').trim(),
        downtime_severity: (String((s as any).downtime_severity || '').trim() === 'alarm') ? 'alarm' : 'alert',
        ...(s.displayName ? { displayName: String(s.displayName).trim() } : {}),
        ...(s.labels ? { labels: s.labels } : {}),
        ...(s.notifications?.teams?.alerts_channel || s.notifications?.teams?.alarms_channel
          ? {
              notifications: {
                ...(s.notifications || {}),
                teams: {
                  ...(s.notifications?.teams || {}),
                  ...(s.notifications?.teams?.alerts_channel ? { alerts_channel: String(s.notifications.teams.alerts_channel).trim() } : {}),
                  ...(s.notifications?.teams?.alarms_channel ? { alarms_channel: String(s.notifications.teams.alarms_channel).trim() } : {}),
                },
              },
            }
          : {}),
      }));
      await apiClient.updateMonitoringTenantConfig(effectiveTenant, next);
      onSaved(effectiveTenant, next);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to save services');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="4xl">
        <DialogHeader>
          <DialogTitle>Services — {effectiveTenant.toUpperCase()}</DialogTitle>
          <DialogDescription>
            Manage <span className="font-mono">monitoring.services</span> (tenant/env/service mapping).
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-48 w-full" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          {!loading && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {services.length} service{services.length !== 1 ? 's' : ''}
                </div>
                <Button variant="outline" size="sm" onClick={addRow} disabled={saving}>
                  Add service
                </Button>
              </div>

              <div className="space-y-2">
                {services.length === 0 && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">No services configured.</div>
                )}

                {services.map((row, idx) => (
                  <div key={idx} className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">serviceId</div>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                          value={row.serviceId}
                          onChange={(e) => updateRow(idx, { serviceId: e.target.value })}
                          placeholder="mas_manage"
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">environment</div>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                          value={row.environment}
                          onChange={(e) => updateRow(idx, { environment: e.target.value })}
                          placeholder="prod"
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">displayName</div>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                          value={row.displayName || ''}
                          onChange={(e) => updateRow(idx, { displayName: e.target.value })}
                          placeholder="MAS Manage"
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">downtime</div>
                        <ResponsiveSelect
                          value={String(row.downtime_severity || 'alert')}
                          onChange={(v) => updateRow(idx, { downtime_severity: (v === 'alarm' ? 'alarm' : 'alert') as any })}
                          disabled={saving}
                          required
                          options={[
                            { value: 'alert', label: 'alert' },
                            { value: 'alarm', label: 'alarm' },
                          ]}
                        />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Teams alerts channel</div>
                        <ResponsiveSelect
                          value={String(row.notifications?.teams?.alerts_channel || '')}
                          onChange={(v) => updateRowTeamsChannel(idx, { alerts_channel: v })}
                          disabled={saving}
                          placeholder="(none)"
                          options={teamsChannels}
                        />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Teams alarms channel</div>
                        <ResponsiveSelect
                          value={String(row.notifications?.teams?.alarms_channel || '')}
                          onChange={(v) => updateRowTeamsChannel(idx, { alarms_channel: v })}
                          disabled={saving}
                          placeholder="(none)"
                          options={teamsChannels}
                        />
                      </div>
                      <div className="md:col-span-6">
                        <div className="text-xs text-gray-600 dark:text-gray-400">url</div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <input
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none font-mono text-sm"
                            value={row.url}
                            onChange={(e) => updateRow(idx, { url: e.target.value })}
                            placeholder="https://..."
                            disabled={saving}
                          />
                          <Button variant="destructive" size="sm" className="sm:flex-shrink-0" onClick={() => removeRow(idx)} disabled={saving}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading}>
            {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


